// CY_BORG soft limits (point-buy style with warnings, GM override still possible)
export const STAT_MIN = -3;
export const STAT_MAX = 6;
export const STAT_POOL_LIMIT = 4; // sum of P/S/A/T/K should not exceed 4
export const STARTING_SATS_MAX = 10000;
export const HP_MAX_CAP = 40;

export const STAT_KEYS = [
  { key: "presence", label: "PRESENCE", abbr: "PRE", accent: "yellow" },
  { key: "strength", label: "STRENGTH", abbr: "STR", accent: "magenta" },
  { key: "agility", label: "AGILITY", abbr: "AGI", accent: "cyan" },
  { key: "toughness", label: "TOUGHNESS", abbr: "TOU", accent: "yellow" },
  { key: "knowledge", label: "KNOWLEDGE", abbr: "KNO", accent: "cyan" },
];

export function emptyCharacter() {
  return {
    id: crypto.randomUUID(),
    name: "UNNAMED PUNK",
    char_class: "",
    presence: 0,
    strength: 0,
    agility: 0,
    toughness: 0,
    knowledge: 0,
    hp_current: 10,
    hp_max: 10,
    sats: 0,
    debt: 0,
    wounds_count: 0,
    glitches_count: 0,
    description: "",
    bad_habits: "",
    worldly_possessions: "",
    weapons: [],
    armor: [],
    nano_powers: [],
    cybertech: [],
    apps: [],
    glitches: [],
    wounds: [],
    gear: [],
    notes: "",
    updated_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
  };
}

export function statSum(c) {
  return (c.presence || 0) + (c.strength || 0) + (c.agility || 0) + (c.toughness || 0) + (c.knowledge || 0);
}

export function validate(c) {
  const warnings = [];
  if (statSum(c) > STAT_POOL_LIMIT) warnings.push(`STAT POOL OVER LIMIT (${statSum(c)}/${STAT_POOL_LIMIT})`);
  for (const s of STAT_KEYS) {
    const v = c[s.key];
    if (v < STAT_MIN || v > STAT_MAX) warnings.push(`${s.label} OUT OF RANGE [${STAT_MIN},${STAT_MAX}]`);
  }
  if (c.sats > STARTING_SATS_MAX) warnings.push(`CREDS OVER STARTING LIMIT (${c.sats}/${STARTING_SATS_MAX})`);
  if (c.hp_max > HP_MAX_CAP) warnings.push(`HP MAX EXCEEDS ${HP_MAX_CAP}`);
  if (c.hp_current > c.hp_max) warnings.push(`HP CURRENT > MAX`);
  return warnings;
}

export const CLASSES = [
  { name: "The Forsaken Gang-Goon", accent: "magenta", desc: "Left for dead by the crew. Melee brute with a chip on their shoulder.",
    // Small & scrappy: Str −2, others 3d6.
    stat_rolls: { presence: {}, strength: { mod: -2 }, agility: {}, toughness: {}, knowledge: {} },
    creds: { count: 2, sides: 6, mult: 10 },
    debt:  { count: 3, sides: 6, mult: 1000 },
    hp:    { sides: 6 } },
  { name: "The Shunned Nanomancer", accent: "cyan", desc: "Excommunicated nano-priest. Trades HP for miracles.",
    // Weird +2 to Presence, Ill −2 to Toughness.
    stat_rolls: { presence: { mod: 2 }, strength: {}, agility: {}, toughness: { mod: -2 }, knowledge: {} },
    creds: { count: 1, sides: 6, mult: 10 },
    debt:  { count: 3, sides: 6, mult: 1000 },
    hp:    { sides: 4 } },
  { name: "The Renegade Cyberslasher", accent: "magenta", desc: "Blade-and-mono chrome punk. Fast, dangerous, deeply broke.",
    // Body & Soul: Str +1, Presence +1. Not a Reader: Knowledge −2.
    stat_rolls: { presence: { mod: 1 }, strength: { mod: 1 }, agility: {}, toughness: {}, knowledge: { mod: -2 } },
    creds: { count: 3, sides: 6, mult: 10 },
    debt:  { count: 3, sides: 6, mult: 1000 },
    hp:    { sides: 10 } },
  { name: "The Burned Hacker", accent: "cyan", desc: "Torched by their last score. Deck-jockey with a grudge.",
    // Cutting Edge: Knowledge +2. Unhealthy Living: Strength −1 & Toughness −1.
    stat_rolls: { presence: {}, strength: { mod: -1 }, agility: {}, toughness: { mod: -1 }, knowledge: { mod: 2 } },
    creds: { count: 2, sides: 6, mult: 10 },
    debt:  { count: 6, sides: 10, mult: 1000 },
    hp:    { sides: 6 } },
  { name: "The Discharged Corp-Killer", accent: "yellow", desc: "Ex-corp asset gone rogue. Trained to kill, wired to feel nothing.",
    // Tough as Nails: Toughness +2. Emotionally scarred: Knowledge −1 & Presence −1.
    stat_rolls: { presence: { mod: -1 }, strength: {}, agility: {}, toughness: { mod: 2 }, knowledge: { mod: -1 } },
    creds: { count: 4, sides: 6, mult: 10 },
    debt:  { count: 3, sides: 6, mult: 1000 },
    hp:    { sides: 10 } },
  { name: "The Orphaned Gear-Head", accent: "yellow", desc: "Wrench monkey raised by scrap. Makes bad tech worse — for enemies.",
    // Engineer: Knowledge +2. Prefers Machines: Presence −2.
    stat_rolls: { presence: { mod: -2 }, strength: {}, agility: {}, toughness: {}, knowledge: { mod: 2 } },
    creds: { count: 2, sides: 6, mult: 10 },
    debt:  { count: 3, sides: 6, mult: 1000 },
    hp:    { sides: 8 } },
  { name: "Classless Punk", accent: "yellow", desc: "No backstory, no perks — just a punk with a grudge. Core-book rules: straight 3d6 abilities, d8+TOU HP.",
    stat_rolls: { presence: {}, strength: {}, agility: {}, toughness: {}, knowledge: {} },
    creds: { count: 3, sides: 6, mult: 10 },
    debt:  { count: 3, sides: 6, mult: 1000 },
    hp:    { sides: 8 },
    glitches: { sides: 2 } },
];

// Default formulas when no class is picked
export const DEFAULT_CREDS_FORMULA = { count: 3, sides: 6, mult: 10 };
export const DEFAULT_DEBT_FORMULA = { count: 3, sides: 6, mult: 1000 };

export function formulaLabel(f) {
  return `${f.count}d${f.sides}×${f.mult.toLocaleString()}`;
}

export function rollFormula(f) {
  const rolls = Array.from({ length: f.count }, () => Math.floor(Math.random() * f.sides) + 1);
  const sum = rolls.reduce((a, b) => a + b, 0);
  return { rolls, sum, total: sum * f.mult };
}

export function credsFormulaFor(className) {
  const cls = CLASSES.find((c) => c.name === className);
  return cls?.creds || DEFAULT_CREDS_FORMULA;
}
export function debtFormulaFor(className) {
  const cls = CLASSES.find((c) => c.name === className);
  return cls?.debt || DEFAULT_DEBT_FORMULA;
}
export function hpDieFor(className) {
  const cls = CLASSES.find((c) => c.name === className);
  return cls?.hp?.sides || 8; // default d8
}

export function glitchDieFor(className) {
  const cls = CLASSES.find((c) => c.name === className);
  return cls?.glitches?.sides || 2;
}
export function rollGlitchesForClass(className) {
  const sides = glitchDieFor(className);
  return { die: sides, value: Math.floor(Math.random() * sides) + 1 };
}

// HP = 1dX + toughness  (X depends on class). Never below 1.
export function rollHpForClass(className, toughness) {
  const sides = hpDieFor(className);
  const die = Math.floor(Math.random() * sides) + 1;
  const total = Math.max(1, die + (toughness || 0));
  return { die, sides, toughness: toughness || 0, total };
}

// Class-flavored weapon + armor loadout pools (matched to each class's stat vibe).
// One weapon + one armor is auto-granted on class select when the character is empty.
export const CLASS_STARTERS = {
  "The Forsaken Gang-Goon": {
    // STR+ TOU+ — melee brute; rough-and-ready
    weapons: ["Machete", "Shotgun", "Broken Bottle", "Throwing Knives"],
    armor:   ["Rough", "StyleGuard"],
  },
  "The Shunned Nanomancer": {
    // PRE+ KNO+ — quick blades, light kit
    weapons: ["Throwing Knives", "Broken Bottle", "Ancient Revolver"],
    armor:   ["No Armor", "StyleGuard"],
  },
  "The Renegade Cyberslasher": {
    // STR+ AGI+ — chrome melee
    weapons: ["Monosword", "Machete", "Throwing Knives"],
    armor:   ["SmartWear", "StyleGuard"],
  },
  "The Burned Hacker": {
    // PRE+ KNO+ — techy sidearm
    weapons: ["Ancient Revolver", "Smartgun", "Throwing Knives"],
    armor:   ["StyleGuard", "SmartWear"],
  },
  "The Discharged Corp-Killer": {
    // STR+ AGI+ TOU+ — pro shooter
    weapons: ["Assault Rifle", "Pulse Rifle", "Shotgun", "Ancient Revolver"],
    armor:   ["Combat Armor", "SmartWear"],
  },
  "The Orphaned Gear-Head": {
    // KNO+ AGI+ — improvised heat
    weapons: ["Smartgun", "Grenade Launcher", "Machete"],
    armor:   ["Rough", "StyleGuard"],
  },
  "Classless Punk": {
    // Random street kit — any common weapon, light armor at best
    weapons: ["Broken Bottle", "Throwing Knives", "Machete", "Ancient Revolver", "Shotgun", "Smartgun"],
    armor:   ["No Armor", "StyleGuard", "Rough"],
  },
};

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// Given a class and an EQUIPMENT catalog, roll one weapon and one armor for the character.
// Returns { weapon, armor } as full character items (with id, text label, damage/tier metadata).
export function rollClassLoadout(className, equipment) {
  const pool = CLASS_STARTERS[className];
  if (!pool || !equipment) return null;
  const wName = pick(pool.weapons);
  const aName = pick(pool.armor);
  const wDef = equipment.find((e) => e.category === "Weapons" && e.name === wName);
  const aDef = equipment.find((e) => e.category === "Armor" && e.name === aName);
  if (!wDef || !aDef) return null;
  const uid = () => (globalThis.crypto?.randomUUID?.() || `it_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  return {
    weapon: {
      id: uid(),
      text: `${wDef.name}${wDef.damage ? ` (${wDef.damage})` : ""}`,
      damage: wDef.damage,
      price: 0, // class kit — free
      category: "Weapons",
      class_kit: true,
    },
    armor: {
      id: uid(),
      text: `${aDef.name}${aDef.tier ? ` [Tier ${aDef.tier}]` : ""}${aDef.note && aDef.note !== "—" ? ` — ${aDef.note}` : ""}`,
      tier: aDef.tier,
      price: 0,
      category: "Armor",
      class_kit: true,
    },
    picked: { weapon: wName, armor: aName },
  };
}

// CY_BORG default stat conversion (3d6 sum → modifier)
export function sumToModifier(sum) {
  if (sum <= 3) return -3;
  if (sum <= 5) return -2;
  if (sum <= 8) return -1;
  if (sum <= 12) return 0;
  if (sum <= 15) return 1;
  if (sum <= 17) return 2;
  return 3;
}

function rollD(sides) { return Math.floor(Math.random() * sides) + 1; }

// Roll a stat with class-specific flat modifier applied to the 3d6 sum.
// Book rule: 3d6 (+N or −N per class) → sum-to-modifier table.
// Returns { sum, modifier, dice: [{label, value}] } for display/history.
export function rollStatForClass(statKey, className) {
  const d1 = rollD(6), d2 = rollD(6), d3 = rollD(6);
  const dice = [
    { label: "d6", value: d1 },
    { label: "d6", value: d2 },
    { label: "d6", value: d3 },
  ];
  let sum = d1 + d2 + d3;
  const cls = CLASSES.find((c) => c.name === className);
  const spec = cls?.stat_rolls?.[statKey] || {};
  const mod = spec.mod || 0;
  if (mod) {
    dice.push({ label: mod > 0 ? `+${mod}` : `${mod}`, value: mod });
    sum += mod;
  }
  return { sum, modifier: sumToModifier(sum), dice };
}

export const CLASS_SUGGESTIONS = CLASSES.map((c) => c.name);

// ---------- Storage / carry capacity ----------
export const BASE_CAPACITY = 4;
export const BACKPACK_BONUS = 6;

export function hasBackpack(c) {
  return (c?.gear || []).some((g) => /backpack/i.test(g.text || g.name || ""));
}
export function storageCapacity(c) {
  return BASE_CAPACITY + (hasBackpack(c) ? BACKPACK_BONUS : 0);
}
export function storageUsed(c) {
  return (c?.gear || []).length;
}

// ---------- Burned Hacker starter (d12 app table) ----------
export const HACKER_APPS = [
  { text: "SNOWCRASH — DoS storm, d4 damage to jacked-in enemies" },
  { text: "KRAKEN — Vacuum data from any node in 6m radius" },
  { text: "GHOST — Invisible on the net for d6 rounds" },
  { text: "ZAP — Micro-EMP fries small electronics within 3m" },
  { text: "RIDDLE — Auto-bypass one password or digital lock" },
  { text: "WARHEAD — Crash target system; user loses next round" },
  { text: "CANDYFLIP — Scramble a target's HUD/optics; DIS on their next attack" },
  { text: "NIGHTINGALE — Sniff all comms in the district for d6 rounds" },
  { text: "BLACKICE — Counter-hack: next intruder into your deck takes d6 damage" },
  { text: "LEECH — Siphon d4 sats from a chipped account nearby (heat if caught)" },
  { text: "LAZARUS — Reboot a bricked piece of cyberware; restores 1 use" },
  { text: "BONESAW — Overwrite a drone/turret's IFF; friendly to you for d4 rounds" },
];

// ---------- Broken table (d8) — rolled when HP hits 0 ----------
export const BROKEN_TABLE = [
  { roll: 1, effect: "COLLAPSE. You must sit. If you use your arms for anything but support, take d4 damage." },
  { roll: 2, effect: "BLEEDING OUT. d4 rounds until you die." },
  { roll: 3, effect: "DEATH RATTLE. DR16 Toughness test or die." },
  { roll: 4, effect: "TORN. You lose d4 HP each hour until treated." },
  { roll: 5, effect: "COMATOSE. Out cold until d6 hours from now." },
  { roll: 6, effect: "LEFT FOR DEAD. Wake up d4 hours later with 1 HP." },
  { roll: 7, effect: "MANGLED. You lose d4 fingers. Permanent −1 Agility." },
  { roll: 8, effect: "TERMINATED. Your punk is dead." },
];

export function rollBroken() {
  const r = Math.floor(Math.random() * 8) + 1;
  return BROKEN_TABLE[r - 1];
}

// Parse a CY_BORG damage notation like "d6", "d10a", "2d10", "d6 (up to d3 targets)"
// into { count, sides, autofire, raw }. Returns null if no die is recognized.
export function parseDamageDie(input) {
  if (!input) return null;
  const s = String(input).trim();
  // Match first NdM (N optional, defaults to 1)
  const m = s.match(/(\d*)d(\d+)/i);
  if (!m) return null;
  const count = m[1] ? parseInt(m[1], 10) : 1;
  const sides = parseInt(m[2], 10);
  if (!sides) return null;
  const autofire = /d\d+a/i.test(s);
  return { count, sides, autofire, raw: s };
}

// Extract damage from a weapon item (either .damage field or parsed from .text)
export function weaponDamageDie(weapon) {
  if (!weapon) return null;
  if (weapon.damage) {
    const parsed = parseDamageDie(weapon.damage);
    if (parsed) return parsed;
  }
  return parseDamageDie(weapon.text);
}

// Roll d12 and return one Hacker starter app object (with UUID id).
export function rollHackerStarterApp() {
  const r = Math.floor(Math.random() * 12) + 1;
  const app = HACKER_APPS[r - 1];
  return { roll: r, id: (globalThis.crypto?.randomUUID?.() || `app_${Date.now()}_${r}`), text: app.text };
}

// ---------- Nano Powers (Shunned Nanomancer starter pack) ----------
export const NANO_POWERS = [
  { text: "AWAKEN — Overload a machine's AI. It obeys you for d4 rounds." },
  { text: "BURN — Deal d6 damage to one target in sight; take d3 backlash." },
  { text: "CLOAK — Static shroud. Invisible for d4 rounds." },
  { text: "DRONE — Hijack a small drone/camera to your side for the scene." },
  { text: "GLIMPSE — Peek d4 seconds ahead. Reroll one d20 this scene." },
  { text: "HEX — Target's next roll suffers disadvantage." },
  { text: "LEECH — Drain d4 HP from a target and add it to your own." },
  { text: "NULL — Cancel one power or app currently targeting you." },
  { text: "PULSE — Fry electronics in 3m; d4 damage to any cyber target." },
  { text: "STAMPEDE — Force one target to flee for d6 rounds." },
];

// Roll N distinct nano powers (default 2) for the Nanomancer starter kit.
export function rollNanoStarterPowers(count = 2) {
  const pool = [...NANO_POWERS];
  const picks = [];
  for (let i = 0; i < count && pool.length; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    const [chosen] = pool.splice(idx, 1);
    picks.push({
      id: (globalThis.crypto?.randomUUID?.() || `nano_${Date.now()}_${i}`),
      text: chosen.text,
    });
  }
  return picks;
}

// ---------- Starting Gear (rolled on class pick) ----------
// Every punk rolls 2 items from this table. If Backpack comes up, capacity expands.
export const STARTING_GEAR_TABLE = [
  { text: "Backpack", note: "Expands carry limit +6" },
  { text: "Multitool" },
  { text: "First-Aid Kit", note: "Heal d4 HP once per scene" },
  { text: "Flashlight" },
  { text: "Lockpicks (Mechanical)" },
  { text: "Faceblock", note: "Blocks facial ID scans for d6 hours" },
  { text: "Zip Ties (×6)" },
  { text: "Spray Can / Marker" },
  { text: "Paracord (10m)" },
  { text: "Crowbar", note: "+d2 to force locks" },
  { text: "Lighter" },
  { text: "Clothes (spare)" },
  { text: "Magnesium Strip" },
  { text: "Superglue" },
  { text: "Grappling-Hook Crossbow" },
  { text: "Foldable Ladder" },
];

// Roll N distinct items from the starting gear table.
export function rollStartingGear(count = 2) {
  const pool = [...STARTING_GEAR_TABLE];
  const picks = [];
  for (let i = 0; i < count && pool.length; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    const [chosen] = pool.splice(idx, 1);
    picks.push({
      id: (globalThis.crypto?.randomUUID?.() || `gear_${Date.now()}_${i}`),
      text: chosen.note ? `${chosen.text} — ${chosen.note}` : chosen.text,
      class_kit: true,
    });
  }
  return picks;
}

// ---------- Punk Name Generator ----------
// Cyberpunk street-handle vibe: adjective/prefix + noun/suffix, or single-word alias.
export const NAME_ADJECTIVES = [
  "Static", "Neon", "Chrome", "Broken", "Rust", "Hollow", "Silent", "Feral",
  "Toxic", "Vapor", "Cold", "Late", "Torn", "Wired", "Ashen", "Glass",
  "Grim", "Sharp", "Fake", "Sick", "Slick", "Numb", "Blank", "Dead",
];
export const NAME_NOUNS = [
  "Wire", "Fang", "Ghost", "Static", "Halo", "Blade", "Fuse", "Vein",
  "Signal", "Screen", "Nail", "Static", "Circuit", "Tooth", "Bone", "Loop",
  "Grid", "Spark", "Node", "Ash", "Rat", "Wolf", "Crow", "Fox",
];
export const NAME_SUFFIXES = [
  "-9", "-X", "-06", "-11", "//", "_v2", "_null", ".exe", "//13", "//alpha",
  "", "", "", "", "", "", "", "", "", "", // empty weight to avoid always suffixing
];
export const NAME_SOLO = [
  "Nyx", "Hex", "Ripper", "Vagrant", "Meridian", "Kilobyte", "Threnody",
  "Fever", "Static", "Junkyard", "Solvent", "Halcyon", "Grifter",
  "Ozone", "Cinder", "Petal", "Voidkid", "Feedback", "Molotov", "Rebar",
];

// ---------- Backlash (Nano miscast) — rolled when a Nano power is pushed too hard (d20) ----------
export const BACKLASH_TABLE = [
  { roll: 1, effect: "STATIC BURN — take d4 damage. Your hair smokes." },
  { roll: 2, effect: "MEMORY GAP — lose the last minute. GM tells you what you missed." },
  { roll: 3, effect: "NANO SEIZE — DIS on your next 2 rolls this scene." },
  { roll: 4, effect: "GHOST FEEDBACK — nearest ally within 3m takes d4 damage from psychic bleed." },
  { roll: 5, effect: "EGO SLIP — −1 Presence for the rest of the session." },
  { roll: 6, effect: "GLITCH SPIKE — gain 1 GLITCH." },
  { roll: 7, effect: "COLLAPSE — you drop to 1 HP if you were above it. Otherwise take d6 more." },
  { roll: 8, effect: "OVERWRITE — one of your nano powers is corrupted; unusable until next long rest." },
  { roll: 9, effect: "NOSE BLEED — you leak fluid from every hole for d4 rounds. −2 to social rolls." },
  { roll: 10, effect: "STATIC WHISPERS — dead voices flood your skull. Cannot rest until dawn." },
  { roll: 11, effect: "MOTOR SEIZE — one limb spasms uncontrollably for d4 rounds. Cannot use it." },
  { roll: 12, effect: "SIGNAL FLARE — nano bloom lights you up like a beacon. Every hostile within 60m knows your position." },
  { roll: 13, effect: "OVERCLOCK BURN — take d8 damage as your synapses cook. Nose scorched." },
  { roll: 14, effect: "LOOP TRAP — repeat the same action for d3 rounds before the loop breaks." },
  { roll: 15, effect: "VOID BLINK — teleport d10 metres in a random direction. Roll for what you smash into." },
  { roll: 16, effect: "ID SLIP — for d6 hours you forget your own name. Answer to whatever last called you." },
  { roll: 17, effect: "ATTRIBUTE DRAIN — −2 to one random stat until next long rest." },
  { roll: 18, effect: "GHOST HAND — a phantom limb writes across your skin. The GM whispers a curse-word to you privately." },
  { roll: 19, effect: "NANO REBOUND — the power you just cast triggers again at a random target within 10m." },
  { roll: 20, effect: "TERMINAL SHOCK — DR16 Toughness or drop to 0 HP. Roll BROKEN." },
];
export function rollBacklash() {
  const r = Math.floor(Math.random() * 20) + 1;
  return BACKLASH_TABLE[r - 1];
}

// ---------- Infestation — long-term nano corruption (d20) ----------
export const INFESTATION_TABLE = [
  { roll: 1, effect: "Second mouth opens where your navel was. Consumed food/drugs count double, but you always look starving." },
  { roll: 2, effect: "Milkwhite eyes see through lies. Once per day, DR8 Presence to sense a lie for the rest of the day." },
  { roll: 3, effect: "Burnt-orange stone skin. Take d4 fewer damage on physical hits, but no defense fumbles." },
  { roll: 4, effect: "Elongated translucent skull. Your brain glows like a candle. Cannot hide at night." },
  { roll: 5, effect: "Scales cover your body. −4DR resisting cold/heat/radiation. −2 Presence in social." },
  { roll: 6, effect: "Leaf-veined arms. You bleed 1 HP per hour in bright sun; +1 to any healing." },
  { roll: 7, effect: "Copper wire hair. Every third day, discharge d4 lightning damage on skin contact." },
  { roll: 8, effect: "Fungal bloom on the neck. Once per session eat the spores to heal d6 — and gain 1 GLITCH." },
  { roll: 9, effect: "Hollow bones. −1 Toughness. +2 to any jumping or climbing test." },
  { roll: 10, effect: "Extra thumb per hand. +1 Agility, but no store-bought gloves ever fit." },
  { roll: 11, effect: "Chemical sweat. Attracts insects. Repels dogs and any drone's scent-sensors." },
  { roll: 12, effect: "Cathedral tongue. You speak in a dead machine language when angry. Cannot hide your emotions." },
  { roll: 13, effect: "Ash-black tears. Every time you weep, one nearby electronic dies." },
  { roll: 14, effect: "Static aura. Rebreathers/masks short out on you. Immune to gas attacks." },
  { roll: 15, effect: "Cracked-mirror skin. Half of light reflects off you. −2 Presence, +2 to stealth in shadow." },
  { roll: 16, effect: "Third lung. Ignore drowning/suffocation for d10 minutes. Coughs up black slurry." },
  { roll: 17, effect: "Extra vertebrae. You can turn your head 270°. −1 Presence, +2 to spot rear ambushes." },
  { roll: 18, effect: "Nano tumours pulse under the skin. Once per session lance one for +d4 HP; take a permanent −1 Toughness." },
  { roll: 19, effect: "Ossified fingernails. Unarmed strikes deal d4. Cannot type on standard keyboards." },
  { roll: 20, effect: "Wrong shadow. Your shadow doesn't match your movements. Anyone watching for a full minute must DR12 Presence or flee." },
];
export function rollInfestation() {
  const r = Math.floor(Math.random() * 20) + 1;
  return INFESTATION_TABLE[r - 1];
}

// ---------- Roll a random item from an array (returns the entry) ----------
export function rollRandom(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function pickFrom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

export function rollPunkName() {
  // 30% chance: single-word alias. 70%: adjective + noun (+ optional suffix).
  if (Math.random() < 0.3) return pickFrom(NAME_SOLO).toUpperCase();
  const adj = pickFrom(NAME_ADJECTIVES);
  const noun = pickFrom(NAME_NOUNS);
  const suf = pickFrom(NAME_SUFFIXES);
  return `${adj} ${noun}${suf}`.toUpperCase();
}
